import moment, { type Moment } from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  CALENDAR_PATH,
  INDIVIDUAL_CALENDAR_PATH,
  MOBILE,
} from '@app/constants';
import { useUserPrint, useWindowSize } from '@app/hooks';
import { useTodayMoment } from '@app/shared/hooks';
import { capitalizeFirstLetter } from '@app/shared/utils';

import { useCalendarSettings } from '@pages/Calendar/hooks';
import { getVisibleCalendarColumns } from '@pages/Calendar/utils/getVisibleColumns';

interface ParsedPeriod {
  periodStartMoment: moment.Moment;
  periodEndMoment: moment.Moment;
  shouldSyncSearchParams: boolean;
}

const createDefaultPeriod = (
  todayMoment: moment.Moment,
  visibleColumns: number
) => ({
  periodStartMoment: todayMoment.clone(),
  periodEndMoment: todayMoment
    .clone()
    .add(Math.max(visibleColumns - 1, 0), 'day'),
});

const parsePeriodParam = (value: string | null) => {
  if (!value) {
    return null;
  }

  const parsedMoment = moment(value, 'YYYY-MM-DD', true);

  if (!parsedMoment.isValid()) {
    return null;
  }

  return parsedMoment.startOf('day');
};

const parseEmployeeIdParam = (
  employeeIdSearchParam: string | null,
  currentUserId: number
) => {
  const parsedEmployeeId = Number(employeeIdSearchParam);

  if (Number.isInteger(parsedEmployeeId) && parsedEmployeeId > 0) {
    return parsedEmployeeId;
  }

  return currentUserId;
};

const formatDate = (dateMoment: Moment) =>
  dateMoment.clone().startOf('day').format('YYYY-MM-DD');

const buildVisibleDays = (
  periodStartMoment: Moment,
  visibleColumns: number
): Moment[] =>
  Array.from({ length: visibleColumns }, (_, index) =>
    periodStartMoment.clone().add(index, 'day')
  );

export const useIndividualCalendar = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { innerWidth } = useWindowSize();
  const {
    params: { UserId },
  } = useUserPrint();
  const [calendarDataRefreshKey, setCalendarDataRefreshKey] = useState(0);
  const [availableGridWidth, setAvailableGridWidth] = useState(0);
  const currentUserId = Number(UserId);
  const {
    intervalHeightPx,
    intervalHeightSize,
    gridStepMinutes,
    maxVisibleColumnCount,
    preferredMaxColumns,
    updateGridStepMinutes,
    updateIntervalHeightSize,
    updateMaxVisibleColumnCount,
    updatePreferredMaxColumns,
    previewFields,
    updatePreviewFields,
  } = useCalendarSettings();

  // Follows the clock: a tab opened yesterday must not keep yesterday as today.
  const todayMoment = useTodayMoment();

  const visibleColumns = useMemo(
    () =>
      getVisibleCalendarColumns({
        availableGridWidth: availableGridWidth || innerWidth,
        preferredMaxColumns,
        isMobile: innerWidth <= MOBILE,
      }),
    [availableGridWidth, innerWidth, preferredMaxColumns]
  );

  const defaultPeriod = useMemo(
    () => createDefaultPeriod(todayMoment, visibleColumns),
    [todayMoment, visibleColumns]
  );

  const dateStartSearchParam = useMemo(
    () => searchParams.get('dateS'),
    [searchParams]
  );

  const dateEndSearchParam = useMemo(
    () => searchParams.get('dateE'),
    [searchParams]
  );

  const employeeIdSearchParam = useMemo(
    () => searchParams.get('employeeId'),
    [searchParams]
  );

  const hasDateSearchParams = useMemo(
    () => Boolean(dateStartSearchParam || dateEndSearchParam),
    [dateEndSearchParam, dateStartSearchParam]
  );

  const currentEmployeeId = useMemo(
    () => parseEmployeeIdParam(employeeIdSearchParam, currentUserId),
    [currentUserId, employeeIdSearchParam]
  );

  const parsedPeriod = useMemo<ParsedPeriod>(() => {
    if (!hasDateSearchParams) {
      return {
        ...defaultPeriod,
        shouldSyncSearchParams: false,
      };
    }

    const parsedStartMoment = parsePeriodParam(dateStartSearchParam);
    const parsedEndMoment = parsePeriodParam(dateEndSearchParam);

    let nextStartMoment = defaultPeriod.periodStartMoment.clone();

    if (parsedStartMoment && parsedEndMoment) {
      if (parsedStartMoment.isAfter(parsedEndMoment, 'day')) {
        return {
          ...defaultPeriod,
          shouldSyncSearchParams: true,
        };
      }

      nextStartMoment = parsedStartMoment;
    } else if (parsedStartMoment) {
      nextStartMoment = parsedStartMoment;
    } else if (parsedEndMoment) {
      nextStartMoment = parsedEndMoment
        .clone()
        .subtract(Math.max(visibleColumns - 1, 0), 'day');
    }

    const normalizedEndMoment = nextStartMoment
      .clone()
      .add(Math.max(visibleColumns - 1, 0), 'day');
    const shouldSyncSearchParams =
      dateStartSearchParam !== formatDate(nextStartMoment) ||
      dateEndSearchParam !== formatDate(normalizedEndMoment);

    return {
      periodStartMoment: nextStartMoment,
      periodEndMoment: normalizedEndMoment,
      shouldSyncSearchParams,
    };
  }, [
    dateEndSearchParam,
    dateStartSearchParam,
    defaultPeriod,
    hasDateSearchParams,
    visibleColumns,
  ]);

  const refreshCalendarData = useCallback(() => {
    setCalendarDataRefreshKey((prev) => prev + 1);
  }, []);

  const updateAvailableGridWidth = useCallback((value: number) => {
    setAvailableGridWidth(value);
  }, []);

  const buildPath = useCallback(
    ({
      employeeId = currentEmployeeId,
      startMoment = parsedPeriod.periodStartMoment,
      endMoment = parsedPeriod.periodEndMoment,
    }: {
      employeeId?: number;
      startMoment?: Moment;
      endMoment?: Moment;
    }) => {
      const nextSearchParams = new URLSearchParams();
      const isDefaultEmployee = employeeId === currentUserId;
      const isDefaultPeriod =
        startMoment.isSame(defaultPeriod.periodStartMoment, 'day') &&
        endMoment.isSame(defaultPeriod.periodEndMoment, 'day');

      if (!isDefaultEmployee) {
        nextSearchParams.set('employeeId', String(employeeId));
      }

      if (!isDefaultPeriod) {
        nextSearchParams.set('dateS', formatDate(startMoment));
        nextSearchParams.set('dateE', formatDate(endMoment));
      }

      const queryString = nextSearchParams.toString();

      return queryString
        ? `${INDIVIDUAL_CALENDAR_PATH}?${queryString}`
        : INDIVIDUAL_CALENDAR_PATH;
    },
    [currentEmployeeId, currentUserId, defaultPeriod, parsedPeriod]
  );

  const navigateToStateHandler = useCallback(
    ({
      employeeId,
      startMoment,
      endMoment,
      replace = false,
    }: {
      employeeId?: number;
      startMoment?: Moment;
      endMoment?: Moment;
      replace?: boolean;
    }) => {
      navigate(buildPath({ employeeId, startMoment, endMoment }), { replace });
    },
    [buildPath, navigate]
  );

  const updatePeriodSearchParamsHandler = useCallback(
    (nextStartMoment: moment.Moment, nextEndMoment?: moment.Moment) => {
      navigateToStateHandler({
        startMoment: nextStartMoment,
        endMoment:
          nextEndMoment ||
          nextStartMoment.clone().add(Math.max(visibleColumns - 1, 0), 'day'),
      });
    },
    [navigateToStateHandler, visibleColumns]
  );

  const goToPrevPeriodHandler = useCallback(() => {
    updatePeriodSearchParamsHandler(
      parsedPeriod.periodStartMoment.clone().subtract(visibleColumns, 'day')
    );
  }, [
    parsedPeriod.periodStartMoment,
    updatePeriodSearchParamsHandler,
    visibleColumns,
  ]);

  const goToNextPeriodHandler = useCallback(() => {
    updatePeriodSearchParamsHandler(
      parsedPeriod.periodStartMoment.clone().add(visibleColumns, 'day')
    );
  }, [
    parsedPeriod.periodStartMoment,
    updatePeriodSearchParamsHandler,
    visibleColumns,
  ]);

  const returnToCurrentPeriodHandler = useCallback(() => {
    navigateToStateHandler({
      startMoment: defaultPeriod.periodStartMoment.clone(),
      endMoment: defaultPeriod.periodEndMoment.clone(),
    });
  }, [defaultPeriod, navigateToStateHandler]);

  const returnToTodayHandler = useCallback(() => {
    navigate(CALENDAR_PATH);
  }, [navigate]);

  const openEmployeePeriodHandler = useCallback(
    (employeeId: number) => {
      navigateToStateHandler({ employeeId });
    },
    [navigateToStateHandler]
  );

  const visibleDays = useMemo(
    () => buildVisibleDays(parsedPeriod.periodStartMoment, visibleColumns),
    [parsedPeriod.periodStartMoment, visibleColumns]
  );

  const isCurrentPeriodSelected = useMemo(
    () =>
      parsedPeriod.periodStartMoment.isSame(
        defaultPeriod.periodStartMoment,
        'day'
      ) &&
      parsedPeriod.periodEndMoment.isSame(defaultPeriod.periodEndMoment, 'day'),
    [
      defaultPeriod,
      parsedPeriod.periodEndMoment,
      parsedPeriod.periodStartMoment,
    ]
  );

  const selectedPeriodTitle = useMemo(() => {
    const startLabel = parsedPeriod.periodStartMoment
      .clone()
      .locale('ru')
      .format('D MMMM');

    const endLabelFormat =
      parsedPeriod.periodStartMoment.year() ===
        parsedPeriod.periodEndMoment.year() &&
      parsedPeriod.periodEndMoment.year() === todayMoment.year()
        ? 'D MMMM'
        : 'D MMMM YYYY';

    const endLabel = parsedPeriod.periodEndMoment
      .clone()
      .locale('ru')
      .format(endLabelFormat);

    return capitalizeFirstLetter(`${startLabel} - ${endLabel}`);
  }, [
    parsedPeriod.periodEndMoment,
    parsedPeriod.periodStartMoment,
    todayMoment,
  ]);

  const validatePeriodSearchParamsHandler = useCallback(() => {
    const shouldClearDefaultDateParams =
      hasDateSearchParams &&
      parsedPeriod.periodStartMoment.isSame(
        defaultPeriod.periodStartMoment,
        'day'
      ) &&
      parsedPeriod.periodEndMoment.isSame(defaultPeriod.periodEndMoment, 'day');
    const shouldClearDefaultEmployeeParam =
      employeeIdSearchParam != null && currentEmployeeId === currentUserId;

    if (
      !parsedPeriod.shouldSyncSearchParams &&
      !shouldClearDefaultDateParams &&
      !shouldClearDefaultEmployeeParam
    ) {
      return;
    }

    navigateToStateHandler({ replace: true });
  }, [
    currentEmployeeId,
    currentUserId,
    defaultPeriod.periodEndMoment,
    defaultPeriod.periodStartMoment,
    employeeIdSearchParam,
    hasDateSearchParams,
    navigateToStateHandler,
    parsedPeriod,
  ]);

  return {
    todayMoment,
    currentEmployeeId,
    visibleDays,
    visibleColumns,
    periodStartMoment: parsedPeriod.periodStartMoment,
    periodEndMoment: parsedPeriod.periodEndMoment,
    selectedPeriodTitle,
    isCurrentPeriodSelected,
    returnToTodayHandler,
    goToPrevPeriodHandler,
    goToNextPeriodHandler,
    openEmployeePeriodHandler,
    returnToCurrentPeriodHandler,
    calendarDataRefreshKey,
    refreshCalendarData,
    intervalHeightPx,
    intervalHeightSize,
    gridStepMinutes,
    maxVisibleColumnCount,
    preferredMaxColumns,
    updateGridStepMinutes,
    updateIntervalHeightSize,
    updateMaxVisibleColumnCount,
    updatePreferredMaxColumns,
    previewFields,
    updatePreviewFields,
    updateAvailableGridWidth,
    validatePeriodSearchParamsHandler,
  };
};
